/**
 * Iframe geometry shared between launcher and SPA. Invariants (see
 * iframeSize.unit.test.ts):
 *  - `FLOATING_EMPTY_HEIGHT_PX > IFRAME_MIN_HEIGHT_PX_FLOATING` — else the
 *    launcher's clamp path treats the shrink emit as the fill-viewport
 *    sentinel and the transparent iframe keeps eating host-page clicks.
 *  - `FLOATING_EMPTY_WITH_ACTIONS_HEIGHT_PX > IFRAME_MIN_HEIGHT_PX_FLOATING`
 *    same reasoning; the actions-row variant is taller, not shorter.
 *  - Both floating-empty heights are strictly less than BASE.
 */

/** Default pane height (both styles). */
export const BASE_IFRAME_HEIGHT_PX = 620;

/**
 * SPA-emitted height when the floating pane has no chat content. Sized to
 * fit the CircuitBox prompt card (~131px) + surrounding iframe padding and
 * the attribution row. Measured from the rendered widget; a smaller value
 * would clip the top of the prompt card at flex-end.
 */
export const FLOATING_EMPTY_HEIGHT_PX = 216;

/**
 * SPA-emitted height when the empty floating pane also has an actions row.
 * AgentActionsRow (~40px) + margin (~8px) stacks above the prompt card.
 */
export const FLOATING_EMPTY_WITH_ACTIONS_HEIGHT_PX = 264;

/** Launcher-side floor for floating style; windowed keeps the 480 floor. */
export const IFRAME_MIN_HEIGHT_PX_FLOATING = 120;

/**
 * Fill-viewport sentinel: the launcher grows the iframe to
 * viewport-minus-chrome when the SPA emits height <= MIN. AUQ uses this
 * to let a tall form breathe.
 */
const FILL_VIEWPORT_SENTINEL_PX = 0;

/** Inputs to {@link computeTargetHeight}. Pure state shape, no callbacks. */
export interface IframeHeightState {
  /** True when the agent config enables `features.actions`. */
  actionsEnabled: boolean;
  /** Latest pill-requested height (or null when no pill open). */
  activePillHeight: number | null;
  chatStyle: 'floating' | 'windowed';
  /** True when ChatScreen has messages / loading / streaming content. */
  hasContent: boolean;
  /** True when the user has toggled the expanded (center) placement. */
  isExpanded: boolean;
  /** True when an AskUserQuestion form is mounted. */
  isPendingToolUse: boolean;
  /** True when the CircuitBox prompt textarea is in its expanded (tall) form. */
  isPromptExpanded: boolean;
}

/**
 * Derive the target iframe height from current UI state. Precedence
 * (highest wins):
 *
 *  1. `activePillHeight != null` → pill-requested height (a popover opened
 *     and asked for a specific pane size).
 *  2. `isPendingToolUse` → 0 (fill-viewport sentinel; launcher grows the
 *     iframe so the wizard has room).
 *  3. `isExpanded` → BASE_IFRAME_HEIGHT_PX (expand only changes placement +
 *     width; the height axis stays at base unless overridden above).
 *  4. `chatStyle === 'floating' && !hasContent && isPromptExpanded` →
 *     BASE_IFRAME_HEIGHT_PX. The prompt textarea grows to its 200px max
 *     which won't fit inside the floating-empty pane (216px minus chrome),
 *     so bump the iframe to full base height until the user collapses again.
 *  5. `chatStyle === 'floating' && !hasContent` →
 *       - actionsEnabled: FLOATING_EMPTY_WITH_ACTIONS_HEIGHT_PX
 *       - else:           FLOATING_EMPTY_HEIGHT_PX
 *  6. Otherwise → BASE_IFRAME_HEIGHT_PX.
 *
 * Windowed never enters the floating-empty branches — its opaque pane has
 * no click-through problem and the launcher's 480 floor would clamp our
 * shrink up anyway.
 */
export function computeTargetHeight(state: IframeHeightState): number {
  if (state.activePillHeight !== null) return state.activePillHeight;
  if (state.isPendingToolUse) return FILL_VIEWPORT_SENTINEL_PX;
  if (state.isExpanded) return BASE_IFRAME_HEIGHT_PX;
  if (state.chatStyle === 'floating' && !state.hasContent) {
    if (state.isPromptExpanded) return BASE_IFRAME_HEIGHT_PX;
    return state.actionsEnabled
      ? FLOATING_EMPTY_WITH_ACTIONS_HEIGHT_PX
      : FLOATING_EMPTY_HEIGHT_PX;
  }
  return BASE_IFRAME_HEIGHT_PX;
}
